/*
 * Copyright (c) 2008-present Sonatype, Inc.
 *
 * All rights reserved. Includes the third-party code listed at http://links.sonatype.com/products/nexus/pro/attributions
 * Sonatype and Sonatype Nexus are trademarks of Sonatype, Inc. Apache Maven is a trademark of the Apache Foundation.
 * M2Eclipse is a trademark of the Eclipse Foundation. All other trademarks are the property of their respective owners.
 */
import Axios from 'axios';
import {Machine, assign} from 'xstate';

import {ExtJS} from 'nexus-ui-plugin';

import UIStrings from '../../../../constants/UIStrings';

export default Machine(
  {
    initial: 'loading',

    context: {
      data: {
        // Configure initial data so we can render the form while it's loading
        idpMetadata: '',
        entityIdUri: '',
        validateResponseSignature: 'default',
        validateAssertionSignature: 'default',
        usernameAttr: 'username',
        firstNameAttr: '',
        lastNameAttr: '',
        emailAttr: '',
        roleAttr: ''
      },
      pristineData: {},
      isPristine: true,
      isEdited: false,
      isValid: true,
      isInvalid: false,
      isEntityIdUri: false,
      error: null
    },

    states: {
      loading: {
        invoke: {
          id: 'fetchData',
          src: 'fetchData',
          onDone: {
            target: 'fetched',
            actions: ['setData', 'checkEdited', 'validate']
          },
          onError: {
            target: 'error',
            actions: ['logLoadError']
          }
        }
      },
      fetched: {
        on: {
          'UPDATE': {
            actions: ['update', 'checkEdited', 'validate']
          },
          'DISCARD': {
            actions: ['discard', 'checkEdited', 'validate']
          },
          'SAVE': {
            target: 'saving'
          }
        }
      },
      saving: {
        invoke: {
          id: 'saveData',
          src: 'saveData',
          onDone: {
            target: 'loading',
            actions: ['logSaveSuccess', 'clearError']
          },
          onError: {
            target: 'fetched',
            actions: ['setError']
          }
        }
      },
      error: {
        type: 'final'
      }
    }
  },
  {
    actions: {
      setData: assign({
        data: (_, {data}) => mapSamlResponse(data),
        pristineData: (_, {data}) => mapSamlResponse(data)
      }),
      update: assign({
        data: (_, {data}) => data
      }),
      checkEdited: assign(({data, pristineData}) => {
        const isPristine = Object.keys(data).every((key) => data[key] === pristineData[key]);
        return {
          isPristine,
          isEdited: !isPristine
        }
      }),
      validate: assign(({data: {idpMetadata, entityIdUri, usernameAttr}}) => {
        const uriRegex = /^[a-z]*:.+$/i;
        const hasRequiredFields = idpMetadata && entityIdUri && usernameAttr;
        const isEntityIdUri = uriRegex.test(entityIdUri);
        return {
          isInvalid: !hasRequiredFields || !isEntityIdUri,
          isEntityIdUri: isEntityIdUri
        };
      }),
      discard: assign({
        data: ({pristineData}) => pristineData
      }),
      logLoadError: ({error}) => {
        if (error) {
          console.error(error);
        }
        ExtJS.showErrorMessage(UIStrings.SAML_CONFIGURATION.MESSAGES.LOAD_ERROR);
      },
      logSaveSuccess: () => {
        ExtJS.showSuccessMessage(UIStrings.SAML_CONFIGURATION.MESSAGES.SAVE_SUCCESS);
      },
      setError: assign({
        error: (_, event) => event.data.response.data
      }),
      clearError: assign({
        error: () => undefined
      })
    },
    services:
      {
        fetchData: () => Axios.get('/service/rest/internal/ui/saml'),
        saveData: ({data}) => {
          return Axios.put('/service/rest/internal/ui/saml', {
            ...data,
            validateResponseSignature: data.validateResponseSignature ===
            'default' ? null : data.validateResponseSignature,
            validateAssertionSignature: data.validateAssertionSignature ===
            'default' ? null : data.validateAssertionSignature
          });
        }
      }
    }
);

/**
 * Returns a modified data object where Boolean properties validateResponseSignature and validateAssertionSignature are
 * handled as strings
 */
function mapSamlResponse({data}) {
  ['validateResponseSignature', 'validateAssertionSignature'].forEach(function(item) {
    data[item] = data[item] === null ? 'default' : data[item].toString();
  });
  return data;
}
